import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../images/logo.png";
import "../styles/LogoAnimation.css";
import BlockchainBackground from "../Components/BlockchainBackground";
import "../styles/BlockchainBackground.css";

const QRScanPage = () => {
    const navigate = useNavigate();
    const [showScanner, setShowScanner] = useState(false);
    const [scanError, setScanError] = useState(null);
    const [isScanning, setIsScanning] = useState(false);
    const containerRef = useRef(null);

    const [displayText, setDisplayText] = useState("");
    const [isTypingComplete, setIsTypingComplete] = useState(false);
    const [showCursor, setShowCursor] = useState(true);
    const fullText = "The Future of Voting.";
    const typingRef = useRef(null);
    const cursorRef = useRef(null);

    // Handle typing animation with better reliability
    useEffect(() => {
        let currentIndex = 0;
        let mounted = true;

        // Clear any existing intervals to prevent issues
        if (typingRef.current) clearInterval(typingRef.current);
        
        // Reset text if component remounts
        setDisplayText("");
        
        // Create new typing interval
        typingRef.current = setInterval(() => {
            if (!mounted) return;
            
            if (currentIndex < fullText.length) {
                setDisplayText(fullText.substring(0, currentIndex + 1));
                currentIndex++;
            } else {
                clearInterval(typingRef.current);
                setIsTypingComplete(true);
            }
        }, 100);

        // Cursor blinking effect
        cursorRef.current = setInterval(() => {
            if (!mounted) return;
            setShowCursor(prev => !prev);
        }, 530);

        return () => {
            mounted = false;
            clearInterval(typingRef.current);
            clearInterval(cursorRef.current);
        };
    }, []);

    const handleScan = async (data) => {
        if (data && !isScanning) {
            setIsScanning(true);
            console.log("Scanned data:", data);

            try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/voter/check`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ token: data }),
                });

                if (!response.ok) {
                    throw new Error("Failed to check voter status");
                }

                const result = await response.json();

                // Redirect based on voter status
                if (result.voted === false) {
                    navigate("/VoterVerifiedPage");
                } else {
                    navigate("/VoterError");
                }
            } catch (error) {
                console.error("Error checking voter status:", error);
                setScanError("Error processing QR Code. Please try again.");
                setIsScanning(false);
            }
        }
    };

    const handleError = (err) => {
        console.error("QR Scan Error:", err);
        setScanError("Could not access camera or read QR code.");
    };

    return (
        <div 
            ref={containerRef}
            className="flex flex-col items-center justify-center min-h-screen bgColor text-center font-[Montserrat] relative overflow-hidden"
        >
            {/* Include the BlockchainBackground component */}
            <BlockchainBackground />

            {/* Card container with glass morphism effect */}
            <div className="relative z-10 flex flex-col items-center bg-[#154c79] bg-opacity-10 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-white border-opacity-20 max-w-md w-full mx-4">
                <div className="relative mb-6">
                    {/* Smaller logo size */}
                    <img 
                        src={logo} 
                        alt="Block Ballot Logo" 
                        className="h-24 mx-auto logo-enhanced hover:scale-105 transition-all duration-500"
                    />
                </div>
                
                {/* Fixed and more reliable typing effect */}
                <div className="h-8 mb-8 flex justify-center items-center">
                    <div className="text-xl font-semibold text-blue-950 flex items-center">
                        <span>{displayText}</span>
                        <span 
                            className={`h-5 border-r-2 border-blue-950 ml-1 ${showCursor ? 'opacity-100' : 'opacity-0'}`} 
                            style={{transitionProperty: 'opacity', transitionDuration: '0.1s'}}
                        ></span>
                    </div>
                </div>
                
                {/* Error message display */}
                {scanError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4 w-full">
                        {scanError}
                    </div>
                )}
                
                {/* Scan QR Code button */}
                <button
                    className="relative overflow-hidden w-full block text-center px-7 py-4 text-lg font-medium text-[#154c79] bg-white rounded-full transition-all duration-300 ease-in-out group hover:translate-y-[-3px] hover:shadow-lg shadow-md"
                    onClick={() => navigate("/VoterScan")} 
                    disabled={isScanning}
                >
                    <span className="relative z-10 transition-colors duration-300 ease-in-out group-hover:text-white flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 group-hover:text-white text-[#154c79]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                        Scan QR Code
                    </span>
                    <span className="absolute inset-0 bg-[#154c79] scale-x-0 origin-left transition-transform duration-500 ease-in-out group-hover:scale-x-100"></span>
                </button>
                
                {/* Secure voting message */}
                <p className="text-sm text-blue-900 mt-6 max-w-xs">
                    Secure, transparent, and immutable voting powered by blockchain technology
                </p>
            </div>

            {/* Footer */}
            <footer className="absolute bottom-4 left-0 w-full text-gray-600 text-sm text-center z-10">
                &copy; BlockBallot. All Rights Reserved.
            </footer>
        </div>
    );
};

export default QRScanPage;